import React, { useState, useMemo } from 'react';
import { Alert, Box, CircularProgress, IconButton, Menu, MenuItem, Typography, Checkbox, Button } from '@mui/material';
import { styled } from '@mui/system';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { incrementWater, decrementWater } from '../../services/dashboard.service';
import { WaterIntakeStats, WaterMeasure } from '../../types/dashboard.types';
import { CalendarEventDto } from '../../types/calendar';
import styles from './WaterIntakeWidget.module.css';
import { AddEventButtons } from '../common/AddEventButtons';
import { toastService } from '../../services/toast.service';

// Styled components from NutritionWidget for event list
const EventLayout = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  padding: 0,
  gap: '8px',
  width: '100%',
}));

const EventContent = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  padding: 0,
  gap: '1px',
  flexGrow: 1,
}));

interface WaterEventItemProps {
  event: CalendarEventDto;
  onEdit: (event: CalendarEventDto) => void;
  onDelete: (eventId: number) => void;
  onToggleComplete: (eventId: number, isCompleted: boolean) => void;
}

const WaterEventItem: React.FC<WaterEventItemProps> = ({ event, onEdit, onDelete, onToggleComplete }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const canEdit = event.actions.includes('edit');
  const canDelete = event.actions.includes('delete');
  const canComplete = event.actions.includes('complete');

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    onEdit(event);
    handleClose();
  };

  const handleDelete = () => {
    onDelete(event.id);
    handleClose();
  };

  const handleToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    onToggleComplete(event.id, e.target.checked);
  };

  const formattedTime = new Date(event.scheduledLdt || '').toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' });
  
  let displayText = event.title;
  if (displayText === 'calendars.defaultFluidTitle' && event.details?.qty) {
    displayText = `Вода, ${event.details.qty} мл`;
  }

  return (
    <EventLayout>
      <Checkbox checked={event.isCompleted} onChange={handleToggle} disabled={!canComplete} />
      <EventContent>
        <Typography variant="body2" sx={{ color: '#282828', fontWeight: 700, fontSize: '15px', lineHeight: '24px' }}>
          {displayText}
        </Typography>
        <Typography variant="caption" sx={{ color: '#A8A8A8', fontWeight: 600, fontSize: '13px', lineHeight: '16px' }}>
          {formattedTime}
        </Typography>
      </EventContent>
      {(canEdit || canDelete) && (
        <>
          <IconButton size="small" sx={{ color: '#D3D3D3' }} onClick={handleClick}>
            <MoreVertIcon />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleClose}
          >
            {canEdit && <MenuItem onClick={handleEdit}>Редагувати</MenuItem>}
            {canDelete && <MenuItem onClick={handleDelete}>Видалити</MenuItem>}
          </Menu>
        </>
      )}
    </EventLayout>
  );
};


interface WaterIntakeWidgetProps {
  selectedDate: string;
  waterMeasures: WaterMeasure[];
  waterStats: WaterIntakeStats | null;
  waterEvents: CalendarEventDto[];
  onDataRefresh: () => void; // Callback to refresh data in the parent
  isLoading: boolean;
  error: string | null;
  onAddLiquid?: () => void;
  onEditWater?: (event: CalendarEventDto) => void;
  onDeleteWater?: (eventId: number) => void;
  onToggleWaterComplete?: (eventId: number, isCompleted: boolean) => void;
}

export const WaterIntakeWidget: React.FC<WaterIntakeWidgetProps> = ({
  selectedDate,
  waterMeasures,
  waterStats,
  waterEvents,
  onDataRefresh,
  isLoading,
  error,
  onAddLiquid,
  onEditWater,
  onDeleteWater,
  onToggleWaterComplete,
}) => {
  const [adding, setAdding] = useState(false);
  const [subtracting, setSubtracting] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isExpanded, setIsExpanded] = useState(false); // State for collapsible events

  const { minVolume, maxVolume } = useMemo(() => {
    if (!waterMeasures || waterMeasures.length === 0) {
      return { minVolume: 0, maxVolume: 0 };
    }
    const values = waterMeasures.map(m => m.value);
    return {
      minVolume: Math.min(...values),
      maxVolume: Math.max(...values),
    };
  }, [waterMeasures]);

  const calculateGlassSize = (value: number) => {
    const minWidth = 25;
    const maxWidth = 40;
    const minHeight = 30;
    const maxHeight = 48;

    if (maxVolume === minVolume) {
      return { width: (minWidth + maxWidth) / 2, height: (minHeight + maxHeight) / 2 };
    }

    const percentage = (value - minVolume) / (maxVolume - minVolume);
    const width = minWidth + (maxWidth - minWidth) * percentage;
    const height = minHeight + (maxHeight - minHeight) * percentage;

    return { width, height };
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleAddWater = async (amount: number) => {
    try {
      setAdding(true);
      const now = new Date();
      const scheduledAt = new Date(`${selectedDate}T${now.toTimeString().slice(0,8)}`);
      await incrementWater(amount, scheduledAt.toISOString());
      toastService.success(`Додано ${amount} мл води`);
      await new Promise(resolve => setTimeout(resolve, 500));
      onDataRefresh();
    } catch (err) {
      console.error('Помилка при додаванні води:', err);
      toastService.error('Не вдалося додати воду');
    } finally {
      setAdding(false);
    }
  };

  const handleSubtractWater = async (amount: number) => {
    handleMenuClose();
    try {
      setSubtracting(true);
      await decrementWater(amount, selectedDate);
      toastService.info(`Віднято ${amount} мл води`);
      await new Promise(resolve => setTimeout(resolve, 500));
      onDataRefresh();
    } catch (err) {
      console.error('Помилка при відніманні води:', err);
      toastService.error('Не вдалося відняти воду');
    } finally {
      setSubtracting(false);
    }
  };

  const handleEdit = (event: CalendarEventDto) => {
    if (onEditWater) onEditWater(event);
  };

  const handleDelete = (eventId: number) => {
    if (onDeleteWater) onDeleteWater(eventId);
  };

  const handleToggleComplete = (eventId: number, isCompleted: boolean) => {
    if (onToggleWaterComplete) {
      onToggleWaterComplete(eventId, isCompleted);
    }
  };

  const getProgressBarColor = (percentage: number) => {
    let hue;
    const saturation = 100;
    const lightness = 50;

    if (percentage <= 100) {
      hue = 220 - (percentage / 100) * 100;
    } else {
      const normalizedOverPercentage = Math.min((percentage - 100) / 25, 1);
      hue = 120 - normalizedOverPercentage * 120;
    }

    hue = Math.max(0, Math.min(360, hue));
    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  if (isLoading) {
    return (
      <div className={styles.card}>
        <div className={styles.loadingContainer}>
          <CircularProgress />
        </div>
      </div>
    );
  }

  if (error && !waterStats) {
    return (
      <div className={styles.card}>
        <Alert severity="error">{error}</Alert>
      </div>
    );
  }

  if (!waterStats) {
    return (
      <div className={styles.card}>
        <Alert severity="info">Дані про споживання води відсутні для цієї дати.</Alert>
      </div>
    );
  }

  const fillPercentage = Math.min(waterStats.percentage, 100);
  const progressBarColor = getProgressBarColor(waterStats.percentage);
  const isActualOverGoal = waterStats.actual > waterStats.goal;

  const displayedEvents = isExpanded ? waterEvents : waterEvents.slice(0, 3);
  const remainingEventsCount = waterEvents.length - displayedEvents.length;

  return (
    <div className={styles.card}>
      <div className={styles.content}>
        <div className={styles.title}>
          <div className={styles.titleLayout}>
            <div className={styles.iconWrapper}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M8 1.33334C8 1.33334 3.33334 6.00001 3.33334 9.33334C3.33334 11.5425 5.12381 13.3333 7.33334 13.3333C9.54286 13.3333 11.3333 11.5425 11.3333 9.33334C11.3333 6.00001 8 1.33334 8 1.33334Z"
                  stroke="#41AEE4"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.titleText}>Трекер рідини</span>
          </div>
        </div>

        <div className={styles.progressSection}>
          <div className={styles.progressBar}>
            <div className={styles.progressBackground} />
            <div
              className={styles.progressFill}
              style={{
                width: `${fillPercentage}%`,
                backgroundColor: progressBarColor,
              }}
            />
          </div>
          <div className={styles.statsRow}>
            <span className={styles.percentage}>{waterStats.percentage}%</span>
            <div className={styles.volumeInfo}>
              <span
                className={styles.actual}
                style={{ color: isActualOverGoal ? 'red' : undefined }}
              >
                {waterStats.actual}
              </span>
              <span className={styles.goal}>/{waterStats.goal}мл</span>
            </div>
          </div>
        </div>

        <div className={styles.tracker}>
          <div>
            <button
              title="Відняти воду"
              className={styles.glassButtonActive}
              onMouseEnter={handleMenuOpen}
              disabled={subtracting || waterStats.actual === 0}
            >
              <div className={styles.glassEmpty}>
                <svg width="30" height="36" viewBox="0 0 30 36" fill="none">
                  <path
                    d="M4 4L8 32H22L26 4H4Z"
                    fill="#F2F2F7"
                    stroke="#F2F2F7"
                    strokeWidth="2"
                  />
                </svg>
                <span className={styles.minusIcon}>−</span>
              </div>
            </button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              MenuListProps={{ onMouseLeave: handleMenuClose }}
            >
              {waterMeasures.map((measure) => (
                <MenuItem key={measure.value} onClick={() => handleSubtractWater(measure.value)}>
                  -{measure.value} {measure.unit}
                </MenuItem>
              ))}
            </Menu>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '16px', justifyContent: 'flex-end', flex: 1, alignItems: 'flex-end' }}>
            {waterMeasures.map((measure) => {
              const size = calculateGlassSize(measure.value);
              return (
                <div key={measure.value} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                  <button
                    title={`Додати ${measure.value} ${measure.unit}`}
                    className={styles.glassButtonActive}
                    onClick={() => handleAddWater(measure.value)}
                    disabled={adding}
                  >
                    <div className={styles.glassFilled} style={{ width: size.width, height: size.height }}>
                      <svg width={size.width} height={size.height} viewBox="0 0 30 36" fill="none" preserveAspectRatio="none">
                        <defs>
                          <linearGradient id={`blueGradient-${measure.value}`} x1="0%" y1="0%" x2="100%" y2="100%">
                            <stop offset="0%" stopColor="#AFF5FF" />
                            <stop offset="100%" stopColor="#2CBBFF" />
                          </linearGradient>
                        </defs>
                        <path
                          d="M4 4L8 32H22L26 4H4Z"
                          fill={`url(#blueGradient-${measure.value})`}
                          stroke={`url(#blueGradient-${measure.value})`}
                          strokeWidth="2"
                        />
                      </svg>
                      <span className={styles.plusIcon}>+</span>
                    </div>
                  </button>
                  <span style={{ fontSize: '12px', color: '#333', fontWeight: '500' }}>{measure.value} {measure.unit}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* Water Events Section */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', width: '100%', mt: 2, p: 2 }}>
        {displayedEvents.length > 0 ? (
          displayedEvents.map((event) => (
            <WaterEventItem
              key={event.id}
              event={event}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onToggleComplete={handleToggleComplete}
            />
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center', width: '100%' }}>
            Поки що немає записів про рідину.
          </Typography>
        )}
        {waterEvents.length > 3 && (
          <Button onClick={() => setIsExpanded(!isExpanded)} sx={{ mt: 1 }}>
            {isExpanded ? 'Показати менше' : `Показати ще ${remainingEventsCount}...`}
          </Button>
        )}
      </Box>
      <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: 'auto' }}>
        <AddEventButtons
          onAddLiquid={onAddLiquid}
          showLiquidButton={true}
          buttonColor="#41AEE4" // Pass the blue color for WaterIntakeWidget
        />
      </Box>
    </div>
  );
};